import { DateTime } from "luxon";
/**
 * Every `LineItemPacingV2` field the API publishes and the name it publishes
 * it under.
 *
 * Written out rather than computed: `satisfies Record<PacingModelFields,
 * string>` makes it exhaustive, so a field added, renamed or removed upstream
 * fails to compile here until this map is updated.
 */
export const PACING_MODEL_TO_SNAKE_MAP = {
    id: "id",
    campaignId: "campaign_id",
    name: "name",
    pacingType: "pacing_type",
    goalInterval: "goal_interval",
    periodGoal: "period_goal",
    perDayGoal: "per_day_goal",
    periodDelivered: "period_delivered",
    periodDeliveredImpressions: "period_delivered_impressions",
    periodDeliveredSpend: "period_delivered_spend",
    periodRemainingImpressions: "period_remaining_impressions",
    periodRemainingBudget: "period_remaining_budget",
    flightGoal: "flight_goal",
    flightDeliveredImpressions: "flight_delivered_impressions",
    flightDeliveredSpend: "flight_delivered_spend",
    flightRemainingImpressions: "flight_remaining_impressions",
    flightRemainingBudget: "flight_remaining_budget",
    stationName: "station_name",
    advertiserName: "advertiser_name",
    advertiserId: "advertiser_id",
    startDate: "start_date",
    endDate: "end_date",
    startDateRaw: "start_raw",
    endDateRaw: "end_raw",
    advertiserCategoryName: "advertiser_category_name",
    advertiserCategoryId: "advertiser_category_id"
};
/**
 * Published fields with no `LineItemPacingV2` key of their own.
 *
 * Campaign identity and dates live on `CampaignPacingV2`, and the product pair
 * is collapsed into a `Map` — but these are all line-item columns in Looker, and
 * a consumer cannot rebuild a campaign row without them. Rows are flat, so they
 * are published per line item and the caller groups.
 */
export const PACING_ROW_FIELD_NAMES = [
    "campaign_name",
    "campaign_start_date",
    "campaign_end_date",
    "campaign_start_raw",
    "campaign_end_raw",
    "product_id",
    "product_name"
];
/**
 * Published fields whose wire value can be a literal `null`, not just an
 * absent key — the gateway passes Looker's raw cell through unconverted, and
 * these are the columns that are nullable struct dereferences or SUM()s over
 * a left join on the Looker side (see `PacingV2QueryResponse`). `name`/
 * `advertiser_name` need no entry here: they already derive `string | null`
 * from `LineItemPacingV2` directly.
 */
export const WIRE_NULLABLE_FIELD_NAMES = [
    "id",
    "campaign_id",
    "advertiser_id",
    "station_name",
    "flight_delivered_impressions",
    "flight_delivered_spend",
    "flight_remaining_impressions",
    "flight_remaining_budget",
    "campaign_name",
    "product_id",
    "product_name"
];
/**
 * Two fields mapped to the same published name would silently drop a key from
 * `PacingRow`, and `satisfies` cannot see that, so the inversion checks it at
 * load. Exported for tests.
 */
export const buildPacingSnakeToModelMap = (modelToSnake) => {
    const map = {};
    const collisions = [];
    for (const field of Object.keys(modelToSnake)) {
        const snake = modelToSnake[field];
        if (snake in map) {
            collisions.push(`${snake} <- ${map[snake]}, ${field}`);
            continue;
        }
        map[snake] = field;
    }
    if (collisions.length > 0) {
        throw new Error(`Pacing field names collide when converted to snake_case: ${collisions.join("; ")}`);
    }
    return map;
};
/** snake_case API field name -> the camelCase `LineItemPacingV2` field. */
export const PACING_SNAKE_TO_MODEL_MAP = buildPacingSnakeToModelMap(PACING_MODEL_TO_SNAKE_MAP);
/** Resolves an API field name, or `undefined` if it names no model field. */
export const pacingToModelField = (name) => PACING_SNAKE_TO_MODEL_MAP[name];
export const pacingToApiField = (field) => PACING_MODEL_TO_SNAKE_MAP[field];
/** Every pacing field name, in `LineItemPacingV2` order, then the row-only columns. */
export const ALL_PACING_FIELD_NAMES = [
    ...Object.keys(PACING_MODEL_TO_SNAKE_MAP).map((field) => PACING_MODEL_TO_SNAKE_MAP[field]),
    ...PACING_ROW_FIELD_NAMES
];
export const PACING_REQUIRED_FILTERS = [
    "analysis_interval"
];
/** The pacing query endpoint, as a path below `/query`. */
export const PACING_ENDPOINT = "/pacing";
/**
 * The sort the screen's own Looker query carries. Purely a truncation concern:
 * the caller regroups and re-sorts, but the sort decides which rows survive the
 * row limit, so dropping it would change *which* campaigns are returned.
 */
export const PACING_SORTS = ["campaign_name"];
/**
 * The row limit the screen's Looker query carries. Sent rather than left to the
 * service default so the two transports truncate at the same row.
 */
export const PACING_ROW_LIMIT = 5000;
/**
 * The analysis window a pacing query runs over: the two dates the screen asked
 * for, validated.
 *
 * Shared by the Looker query builder and the endpoint request builder, so the
 * two accept the same inputs and refuse with the same message rather than
 * drifting into two answers. Only the formatting differs — slashes for Looker,
 * dashes for the endpoint.
 *
 * The screen sends its range as an `EQ` filter over a `Date[]` rather than a
 * `BETWEEN`, so the operator is not checked; the value shape is. Non-Date
 * members are dropped rather than refused, which is what the Looker path has
 * always done.
 */
export const resolvePacingDateRange = (filters) => {
    const dates = filters.where?.find((filter) => filter.field === "dates")?.value;
    if (!dates) {
        throw new Error("Dates filter is required");
    }
    if (!Array.isArray(dates)) {
        throw new Error("Dates filter must be an array");
    }
    const range = dates.filter((date) => date instanceof Date);
    if (range.length !== 2) {
        throw new Error("Dates filter must contain two dates");
    }
    return range;
};
/**
 * The interval as the API takes it: dashes, and **inclusive at both ends**.
 *
 * `explore_pacing_v2` treats the analysis interval as inclusive and the service
 * passes it through, so — unlike attribution — the end date is not shifted. The
 * screen's default range ends today and the screen exists to show current-day
 * delivery, so incrementing here would query a day into the future.
 */
const toAnalysisInterval = (range) => range.map((date) => DateTime.fromJSDate(date).toFormat("yyyy-MM-dd")).join(" to ");
/**
 * Translates the filters the pacing screen hands `findItems` into the request
 * the endpoint takes.
 *
 * Every published field is requested, because the screen's Looker query selects
 * every column it can group, label, filter and sort by. The one column that
 * query selects and this one cannot is `vw_fct_impressions.pacing_status`, which
 * the screen fetches and discards — `derivePacing` recomputes pace from
 * `periodDelivered / periodGoal`.
 *
 * The campaign and line-item status filters are absent because the service pins
 * them itself; sending them would earn a 400. No advertiser scope is sent
 * either: the screen filters on a date range alone, and pacing is the documented
 * exception to the advertiser-scope convention.
 */
export const toPacingApiRequest = (filters) => ({
    fields: [...ALL_PACING_FIELD_NAMES],
    filters: { analysis_interval: toAnalysisInterval(resolvePacingDateRange(filters)) },
    sorts: [...PACING_SORTS],
    limit: PACING_ROW_LIMIT
});
//# sourceMappingURL=api.js.map